import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import http from '../../lib/http';
import ConfirmModal from '../../components/ui/ConfirmModal';
import AnimatedModal from '../../components/ui/AnimatedModal';
import {
  BuildingOffice2Icon,
  ClockIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  UserCircleIcon,
  EnvelopeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const ACCEPTED = '.pdf,.jpg,.jpeg,.png';
const DOCUMENT_RETENTION_DAYS = 180;

const UPLOAD_HINTS = {
  'resume::resume_cv':
    'Upload your latest resume / CV as a PDF or high-resolution image (JPEG/PNG).',
  'education::degree_certificate':
    'Provide a legible copy of your degree certificate. PDF or high-resolution image (JPEG/PNG).',
  'education::marksheet':
    'Provide a legible copy of your marksheet. Ensure all pages are readable.',
  'education::provisional_certificate':
    'Provide your provisional certificate if the final degree certificate is not yet available.',
  'employment::experience_letter':
    'Upload a clear copy of your experience letter, relieving letter, or service certificate, as applicable.',
  'employment::offer_letter':
    'Upload a clear copy of your offer letter from the relevant employer.',
  'salary::salary_slips':
    'Upload salary slips for the period requested. Combine multiple pages into one PDF where possible.',
  'salary::form_16':
    'Upload Form 16 or equivalent tax statement as a legible PDF or image.',
  'address::utility_bill':
    'Upload a recent utility bill showing your name and address (electricity, water, or gas, as accepted by the employer).',
  'address::rental_agreement':
    'Upload a legible copy of your rental agreement or equivalent address proof.',
  'professional::professional_license':
    'Upload a clear copy of your professional license or certification.',
};

function getUploadHint(doc) {
  const k = `${doc.type}::${doc.subtype}`;
  return (
    UPLOAD_HINTS[k] ||
    'Upload a clear, legible file in PDF, JPEG, or PNG format (maximum size as permitted by the portal).'
  );
}

function formatDate(d) {
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    return dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

function SectionCard({ icon: Icon, title, children, className = '' }) {
  return (
    <section className={`rounded-lg border border-slate-200 bg-white ${className}`}>
      <div className="border-b border-slate-100 bg-slate-50/80 px-4 py-3 sm:px-5">
        <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">
          {Icon && <Icon className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />}
          {title}
        </h2>
      </div>
      <div className="px-4 py-4 text-sm leading-relaxed text-slate-700 sm:px-5 sm:py-5">{children}</div>
    </section>
  );
}

export default function CandidateDocumentUpload() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [data, setData] = useState(null);
  const [consent, setConsent] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');
  const [submittingAll, setSubmittingAll] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});
  const fileInputRefs = useRef({});

  const uploadedKeySet = useMemo(() => {
    const set = new Set();
    (data?.uploadedDocuments || []).forEach((d) => set.add(`${d.type}::${d.subtype}`));
    return set;
  }, [data]);

  // Derivations must be defined before any early return (hooks rule).
  const companyName = data?.companyName || 'the organisation';
  const jobTitle = data?.jobTitle || 'the role';
  const requested = data?.requestedDocuments || [];
  const uploaded = data?.uploadedDocuments || [];

  const status = data?.status || 'pending';
  const allSubmitted = status === 'submitted';

  const pendingDocs = useMemo(() => {
    const list = [];
    (requested || []).forEach((doc) => {
      const key = `${doc.type}::${doc.subtype}`;
      if (!uploadedKeySet.has(key)) list.push(doc);
    });
    return list;
  }, [requested, uploadedKeySet]);

  const missingSelection = useMemo(() => {
    const missing = [];
    pendingDocs.forEach((doc) => {
      const key = `${doc.type}::${doc.subtype}`;
      if (!selectedFiles[key]) missing.push(doc);
    });
    return missing;
  }, [pendingDocs, selectedFiles]);

  const readyToSubmit = useMemo(() => {
    // We only enable the final submit once:
    // - all required files are selected (so candidate knows what's being uploaded)
    // - consent was explicitly accepted
    return pendingDocs.length > 0 && missingSelection.length === 0;
  }, [pendingDocs.length, missingSelection.length]);

  const refresh = async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await http.get(`/api/documents/upload/${token}`);
      setData(res.data);
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.message ||
          'This link has expired or is invalid. If you need assistance, contact the employer or support@talentcrux.ai.'
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleUpload = async (doc) => {
    const key = `${doc.type}::${doc.subtype}`;
    const file = selectedFiles[key];
    if (!file) {
      toast.error('Select a file before uploading.');
      return;
    }
    if (!consent) {
      toast.error('Please confirm your consent below before uploading.');
      return;
    }
    setUploadingKey(key);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('type', doc.type);
      fd.append('subtype', doc.subtype);
      fd.append('label', doc.label);
      fd.append('consentGiven', 'true');
      fd.append('consentIp', '');
      await http.post(`/api/documents/upload/${token}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Document uploaded securely.');
      setSelectedFiles((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      const input = fileInputRefs.current[key];
      if (input) input.value = '';
      await refresh();
      // If this was the last pending document, trigger completion & notifications
      if (pendingDocs.length <= 1) {
        try {
          await http.post(`/api/documents/upload/${token}/complete`);
        } catch (_) {}
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Upload could not be completed.');
    } finally {
      setUploadingKey('');
    }
  };

  const handleSubmitAll = async () => {
    if (!consent) {
      toast.error('Please confirm your consent before submitting documents.');
      return;
    }
    if (pendingDocs.length === 0) {
      toast.success('All requested documents have already been submitted.');
      return;
    }
    if (missingSelection.length > 0) {
      const names = missingSelection.map((d) => d.label).slice(0, 4).join(', ');
      toast.error(
        `Please select files for all pending items before submitting. Missing: ${names}${
          missingSelection.length > 4 ? '…' : ''
        }`
      );
      return;
    }
    setSubmittingAll(true);
    try {
      for (const doc of pendingDocs) {
        // eslint-disable-next-line no-await-in-loop
        await handleUpload(doc);
      }
      try {
        await http.post(`/api/documents/upload/${token}/complete`);
      } catch (_) {}
      toast.success('Submission completed.');
    } finally {
      setSubmittingAll(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!token) return;
    setDeletingAll(true);
    try {
      await http.delete(`/api/documents/upload/${token}`);
      toast.success('Your documents have been deleted.');
      setShowDeleteConfirm(false);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Deletion could not be completed.');
    } finally {
      setDeletingAll(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mx-auto mb-4 h-10 w-10 rounded-full border-2 border-slate-200 border-t-slate-600 animate-spin" />
          <p className="text-center text-sm font-medium text-slate-600">Loading secure portal…</p>
        </div>
      </div>
    );
  }

  if (errorMsg) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-base font-semibold text-slate-900">Link not available</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">{errorMsg}</p>
          <p className="mt-6 text-xs leading-relaxed text-slate-500 border-t border-slate-100 pt-4">
            TalentCrux — Secure Document Collection. Processing in accordance with the Digital Personal
            Data Protection Act, 2023 (India), where applicable.
          </p>
        </div>
      </div>
    );
  }

  if (data?.status === 'deleted') {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-lg rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
          <h1 className="text-base font-semibold text-slate-900">Documents removed</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            Your submitted documents have been deleted from this collection workflow. No further action is
            required on this page.
          </p>
          <p className="mt-6 text-xs text-slate-500">
            TalentCrux — Secure Document Collection Platform. Data processed in accordance with applicable
            law, including the Digital Personal Data Protection Act, 2023 (India), where applicable.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 px-3 py-8 sm:px-5 sm:py-10 lg:px-8">
      <div className="mx-auto w-full max-w-7xl">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              Powered by
            </span>
            <div className="flex items-center gap-2">
              <img src="/logo.svg" alt="TalentCrux" className="h-7 w-7 object-contain" />
              <span className="text-sm font-extrabold tracking-tight text-slate-900">
                TalentCrux
              </span>
            </div>
          </div>
        </div>
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          {!allSubmitted ? (
            <div className="lg:grid lg:grid-cols-12 lg:items-start">
              {/* Left: blue header + company/role details */}
              <div className="p-5 sm:p-6 lg:col-span-7 lg:p-8 lg:pr-4">
                <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                  <div className="bg-[#007BFF] px-5 py-5 sm:px-6">
                    <h1 className="mt-2 text-xl font-semibold text-white">
                      {companyName}
                    </h1>
                    <p className="mt-1 text-sm text-white/90">
                      Position: <span className="font-semibold text-white">{jobTitle}</span>
                    </p>
                  </div>
                  <div className="px-5 py-5 sm:px-6 space-y-3">
                    <p className="text-sm leading-relaxed text-slate-700">
                      <strong className="text-slate-900">{companyName}</strong> has requested that you submit
                      documents for your application for the position above.
                    </p>
                    <p className="text-sm leading-relaxed text-slate-700">
                      This secure portal is operated by <strong className="text-slate-900">TalentCrux</strong>{' '}
                      on behalf of <strong className="text-slate-900">{companyName}</strong> as part of
                      recruitment and employment-related verification (including background verification where
                      applicable).
                    </p>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                      Step 1: Attach the required documents on the right. <br />
                      Step 2: Review and accept consent. <br />
                      Step 3: Submit your documents.
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowConsentModal(true)}
                      className="inline-flex items-center justify-center rounded-md border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-900 shadow-sm hover:bg-slate-50"
                    >
                      View consent & policies (DPDP Act)
                    </button>
                  </div>
                </div>
                <div className="mt-8 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
                  <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">
                    <EnvelopeIcon className="h-4 w-4 text-slate-500" aria-hidden />
                    Support
                  </h3>
                  <p className="mt-2 text-sm text-slate-700">
                    For technical assistance with this portal, contact{' '}
                    <a
                      href="mailto:support@talentcrux.ai"
                      className="font-medium text-slate-900 underline underline-offset-2 hover:text-slate-700"
                    >
                      support@talentcrux.ai
                    </a>
                    . For questions about the role or employer requirements, please contact the hiring
                    organisation directly.
                  </p>
                </div>
              </div>

              {/* Right: uploads + support — sticky on large screens */}
              <aside className="border-t border-slate-200 bg-slate-50/40 p-5 sm:p-6 lg:col-span-5 lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto lg:border-l lg:border-t-0 lg:px-8 lg:py-8">
                <div className="mb-4 flex items-start justify-between gap-3 border-b border-slate-200 pb-3">
                  <div className="flex items-center gap-2">
                  <DocumentTextIcon className="h-5 w-5 text-slate-500" aria-hidden />
                  <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-slate-800">
                    Upload required documents
                  </h2>
                  </div>
                  <button
                    type="button"
                    onClick={handleSubmitAll}
                    disabled={!consent || !readyToSubmit || submittingAll}
                    className={`shrink-0 rounded-lg px-4 py-2 text-sm font-semibold shadow-sm transition focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 ${
                      !consent || !readyToSubmit || submittingAll
                        ? 'cursor-not-allowed bg-slate-200 text-slate-500'
                        : 'bg-slate-900 text-white hover:bg-slate-800'
                    }`}
                  >
                    {submittingAll ? 'Submitting…' : 'Submit documents'}
                  </button>
                </div>
                <p className="mb-5 text-xs text-slate-500">
                  Accepted formats: PDF, JPEG, PNG (max. 10 MB per file, unless otherwise stated by your
                  employer).
                </p>
                {pendingDocs.length > 0 && missingSelection.length > 0 && (
                  <div className="mb-4 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    Select files for all requested items to enable the submit button.
                  </div>
                )}

                <div className="space-y-4">
                  {requested.map((doc) => {
                    const key = `${doc.type}::${doc.subtype}`;
                    const isUploaded = uploadedKeySet.has(key);
                    const uploadedDoc = uploaded.find((d) => `${d.type}::${d.subtype}` === key);
                    const hint = getUploadHint(doc);
                    const selectedName = selectedFiles[key]?.name;

                    return (
                      <div
                        key={key}
                        className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
                      >
                        <div className="text-sm font-semibold text-slate-900">{doc.label}</div>
                        <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{hint}</p>
                        {isUploaded && uploadedDoc?.uploadedAt && (
                          <p className="mt-2 text-xs text-slate-500">
                            Received on {formatDate(uploadedDoc.uploadedAt)}
                          </p>
                        )}

                        {isUploaded ? (
                          <div className="mt-4 inline-flex items-center rounded border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-900">
                            Submitted
                          </div>
                        ) : (
                          <div className="mt-4 flex flex-col gap-2">
                            <div className="min-w-0">
                              <input
                                ref={(el) => {
                                  fileInputRefs.current[key] = el;
                                }}
                                id={`doc-file-${key}`}
                                type="file"
                                accept={ACCEPTED}
                                className="sr-only"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (!f) return;
                                  setSelectedFiles((prev) => ({ ...prev, [key]: f }));
                                }}
                              />
                              <div className="flex flex-wrap items-center gap-2">
                                <label
                                  htmlFor={`doc-file-${key}`}
                                  className="inline-flex cursor-pointer items-center rounded border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-800 shadow-sm transition hover:bg-slate-50"
                                >
                                  Select file
                                </label>
                                <span className="min-w-0 max-w-full truncate text-xs text-slate-600 sm:max-w-md">
                                  {selectedName || 'No file selected'}
                                </span>
                              </div>
                            </div>
                            <p className="text-[11px] text-slate-500">
                              This item will be uploaded when you click <strong>Submit documents</strong>.
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Consent step: shown after files are selected */}
                {readyToSubmit && (
                  <div className="mt-6 rounded-lg border border-slate-200 bg-white p-4 sm:p-5">
                    <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-700">
                      <ShieldCheckIcon className="h-4 w-4 text-slate-500" aria-hidden />
                      Consent
                    </h3>
                    <p className="mt-2 text-sm text-slate-700">
                      After attaching your documents, please review and accept the consent & policies before submitting.
                    </p>
                    <label
                      className="mt-3 flex cursor-pointer items-start gap-3 rounded-md border border-slate-200 bg-slate-50 p-3"
                      onClick={(e) => {
                        // Always show modal first. Only "Accept" sets consent true.
                        if (!consent) {
                          e.preventDefault();
                          setShowConsentModal(true);
                        }
                      }}
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-400 text-slate-900 focus:ring-slate-500"
                        checked={consent}
                        onChange={() => {
                          if (consent) return;
                          setShowConsentModal(true);
                        }}
                      />
                      <span className="text-sm leading-relaxed text-slate-800">
                        I have read the consent & policies and I accept.
                      </span>
                    </label>
                    {!consent && (
                      <p className="mt-2 text-xs text-slate-500">
                        Clicking the checkbox will open the consent & policies modal.
                      </p>
                    )}
                  </div>
                )}

                
              </aside>
            </div>
          ) : (
            <div className="space-y-6 p-5 sm:p-8 lg:p-10">
              <div>
                <div className="mb-5 flex items-center gap-2 border-b border-slate-200 pb-3">
                  <DocumentTextIcon className="h-5 w-5 text-slate-500" aria-hidden />
                  <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-slate-800">
                    Submitted documents
                  </h2>
                </div>
                <p className="mb-6 text-xs text-slate-500">
                  Accepted formats: PDF, JPEG, PNG (max. 10 MB per file, unless otherwise stated by your
                  employer).
                </p>

                <div className="grid gap-4 sm:grid-cols-1 lg:grid-cols-2">
                  {requested.map((doc) => {
                    const key = `${doc.type}::${doc.subtype}`;
                    const isUploaded = uploadedKeySet.has(key);
                    const uploadedDoc = uploaded.find((d) => `${d.type}::${d.subtype}` === key);
                    const hint = getUploadHint(doc);

                    return (
                      <div
                        key={key}
                        className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 sm:p-5"
                      >
                        <div className="text-sm font-semibold text-slate-900">{doc.label}</div>
                        <p className="mt-1.5 text-xs leading-relaxed text-slate-600">{hint}</p>
                        {isUploaded && uploadedDoc?.uploadedAt && (
                          <p className="mt-2 text-xs text-slate-500">
                            Received on {formatDate(uploadedDoc.uploadedAt)}
                          </p>
                        )}
                        {isUploaded ? (
                          <div className="mt-4 inline-flex items-center rounded border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-900">
                            Submitted
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg border border-amber-200 bg-amber-50 p-5">
                <h3 className="text-sm font-semibold text-amber-950">Delete submitted documents</h3>
                <p className="mt-2 text-sm leading-relaxed text-amber-950/90">
                  You may request permanent deletion of your uploaded files from this collection. The employer
                  may be notified. This action may affect your application if documents are still required.
                </p>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="mt-4 rounded border border-red-300 bg-white px-4 py-2.5 text-sm font-semibold text-red-800 shadow-sm transition hover:bg-red-50"
                >
                  Request deletion of my documents
                </button>
              </div>
            </div>
          )}
        </div>

        <footer className="mt-8 border-t border-slate-200 pt-6 text-center">
          <div className="flex justify-center">
            <BuildingOffice2Icon className="h-5 w-5 text-slate-400" aria-hidden />
          </div>
          <p className="mt-2 text-xs font-medium text-slate-600">
            Powered by TalentCrux — Secure Document Collection Platform
          </p>
          <p className="mx-auto mt-3 max-w-3xl px-4 text-[11px] leading-relaxed text-slate-500">
            Personal data collected through this portal is processed in accordance with the Digital Personal
            Data Protection Act, 2023 (India) and applicable law. TalentCrux acts as a data processor to the
            employer (data fiduciary) for this workflow.
          </p>
        </footer>

        <ConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => (deletingAll ? null : setShowDeleteConfirm(false))}
          onConfirm={handleDeleteAll}
          title="Request deletion of submitted documents?"
          message="This will permanently delete the documents you uploaded using this secure link and may affect processing of your application."
          confirmLabel="Delete documents"
          cancelLabel="Cancel"
          variant="danger"
          loading={deletingAll}
        />

        <AnimatedModal
          isOpen={showConsentModal}
          onClose={() => setShowConsentModal(false)}
          backdropClassName="bg-black/50"
          contentClassName="w-full max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 max-h-[90vh] flex flex-col"
          zIndex={60}
          ariaLabelledBy="consent-modal-title"
        >
          <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-200 bg-white">
            <div className="min-w-0">
              <h2 id="consent-modal-title" className="text-base font-semibold text-slate-900">
                Consent & policies
              </h2>
              <p className="mt-1 text-xs text-slate-500">
                Please review the information below (DPDP Act, 2023 where applicable).
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowConsentModal(false)}
              className="text-slate-400 hover:text-slate-600"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-5 py-5 bg-slate-50/60 space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <SectionCard icon={DocumentTextIcon} title="Purpose of data collection">
                <p className="mb-3">The documents you provide will be used solely for:</p>
                <ul className="list-disc space-y-1 pl-5 text-slate-700">
                  <li>Recruitment and selection, including evaluation of your application; and</li>
                  <li>Background or employment verification, where applicable.</li>
                </ul>
                <p className="mt-4 text-slate-700">
                  Access is limited to personnel and systems authorised by <strong>{companyName}</strong> and
                  its designated verification partners acting on instructions in line with this purpose.
                </p>
              </SectionCard>

              <SectionCard icon={LockClosedIcon} title="Data security">
                <p>
                  Documents are transmitted over encrypted connections and stored using industry-standard
                  safeguards (including encryption and access controls). Processing is carried out in line
                  with reasonable security practices and applicable data protection law.
                </p>
              </SectionCard>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <SectionCard icon={UserCircleIcon} title="Your rights">
                <p>
                  You may access, update, or request deletion of your submitted documents through this secure
                  link, subject to the employer&apos;s legitimate recruitment interests and applicable law
                  (including retention obligations). For grievances relating to this processing, you may also
                  contact the Data Protection Board of India as provided under law when such mechanisms apply.
                </p>
              </SectionCard>

              <SectionCard icon={ClockIcon} title="Data retention">
                <p>
                  Uploaded documents are retained for a maximum of{' '}
                  <strong>{DOCUMENT_RETENTION_DAYS} days</strong> from upload unless a shorter period is applied
                  earlier, or longer retention is required by law or by the employer for a lawful purpose.
                  After the retention period, documents are securely deleted in line with TalentCrux&apos;s
                  retention procedures.
                </p>
              </SectionCard>
            </div>

            <SectionCard icon={ShieldCheckIcon} title="Consent">
              <p className="mb-4 text-slate-700">
                Pursuant to the Digital Personal Data Protection Act, 2023 (India) and applicable rules, your
                consent is required before your documents are collected and processed for the purposes described
                above.
              </p>
              <div className="rounded-md border border-slate-300 bg-white p-4 text-sm leading-relaxed text-slate-800">
                I hereby confirm that I voluntarily consent to the collection, processing, and sharing of the
                documents I submit with <strong>{companyName}</strong> and authorised verification partners,
                strictly for recruitment and employment verification purposes, in accordance with applicable
                data protection laws (including the Digital Personal Data Protection Act, 2023 (India), where
                applicable). I understand I may withdraw consent or request access or erasure where permitted
                by law using the mechanisms described above.
              </div>
            </SectionCard>
          </div>

          <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-200 bg-white">
            <button
              type="button"
              onClick={() => setShowConsentModal(false)}
              className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-slate-50"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => {
                setConsent(true);
                setShowConsentModal(false);
              }}
              className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm ${
                'bg-slate-900 text-white hover:bg-slate-800'
              }`}
            >
              Accept & continue
            </button>
          </div>
        </AnimatedModal>
      </div>
    </div>
  );
}
